/** Card filtering, sorting and grouping for board views. No store or Vue dependencies. */
import type {ICard, ICardViewState} from './board_demo_data_types';
import {CardGroupBy, CardPriority, CardSortBy, CardStatus} from './board_demo_data_types';

/** Group key used for cards with no assignee when grouping by assignee. */
export const UNASSIGNED_GROUP_KEY = 'unassigned';

/** Sort weight per priority — lower sorts first. */
export const PRIORITY_RANK: Record<CardPriority, number> = {
   [CardPriority.CRITICAL]: 0,
   [CardPriority.HIGH]: 1,
   [CardPriority.MEDIUM]: 2,
   [CardPriority.LOW]: 3,
};

/** Group order for the status and priority group-by modes. */
export const STATUS_ORDER: readonly CardStatus[] = [
   CardStatus.TODO,
   CardStatus.IN_PROGRESS,
   CardStatus.DONE,
];
export const PRIORITY_ORDER: readonly CardPriority[] = [
   CardPriority.CRITICAL,
   CardPriority.HIGH,
   CardPriority.MEDIUM,
   CardPriority.LOW,
];

/**
 * A group of cards. `key` is the CardStatus, CardPriority, member id or
 * UNASSIGNED_GROUP_KEY for the group-by mode; labels and colors are resolved by the UI.
 */
export interface ICardGroup {
   key: string;
   cards: ICard[];
}

/**
 * Apply the full view state (query + filters + sort) to a card list.
 * Returns a new array; the input is never mutated.
 */
export function applyCardView(cards: readonly ICard[], view: ICardViewState): ICard[] {
   return sortCards(filterCards(cards, view), view.sortBy);
}

/**
 * Filter cards by the view's query and filter selections.
 */
export function filterCards(cards: readonly ICard[], view: ICardViewState): ICard[] {
   const query = view.query.trim().toLowerCase();

   return cards.filter((card) => {
      if (query.length > 0) {
         const matches_query =
            card.title.toLowerCase().includes(query) ||
            card.description.toLowerCase().includes(query);
         if (!matches_query) {
            return false;
         }
      }
      if (view.filterPriority != null && card.priority !== view.filterPriority) {
         return false;
      }
      if (view.filterLabelId != null && !card.labelIds.includes(view.filterLabelId)) {
         return false;
      }
      if (view.filterAssigneeId != null && card.assigneeId !== view.filterAssigneeId) {
         return false;
      }
      return true;
   });
}

/**
 * Return the cards sorted per the requested mode (MANUAL keeps input order).
 * Always returns a new array.
 */
export function sortCards(cards: readonly ICard[], sort_by: CardSortBy): ICard[] {
   const sorted = [...cards];

   switch (sort_by) {
      case CardSortBy.PRIORITY: {
         sorted.sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]);
         break;
      }
      case CardSortBy.DUE: {
         // ISO YYYY-MM-DD compares correctly as a string; cards without a due
         // date sort last.
         sorted.sort((a, b) => (a.dueDate ?? '9999-99-99').localeCompare(b.dueDate ?? '9999-99-99'));
         break;
      }
      case CardSortBy.TITLE: {
         sorted.sort((a, b) => a.title.localeCompare(b.title));
         break;
      }
      case CardSortBy.MANUAL:
      default: {
         break;
      }
   }

   return sorted;
}

/**
 * Group cards for the group-by mode: STATUS_ORDER, PRIORITY_ORDER, or board
 * members in the given order plus a final UNASSIGNED_GROUP_KEY group. Empty
 * groups are included.
 */
export function groupCards(
   cards: readonly ICard[],
   group_by: CardGroupBy,
   board_member_ids: readonly string[],
): ICardGroup[] {
   if (group_by === CardGroupBy.PRIORITY) {
      return PRIORITY_ORDER.map((priority) => ({
         key: priority,
         cards: cards.filter((c) => c.priority === priority),
      }));
   }

   if (group_by === CardGroupBy.ASSIGNEE) {
      const groups: ICardGroup[] = board_member_ids.map((member_id) => ({
         key: member_id,
         cards: cards.filter((c) => c.assigneeId === member_id),
      }));
      groups.push({
         key: UNASSIGNED_GROUP_KEY,
         cards: cards.filter((c) => c.assigneeId == null),
      });
      return groups;
   }

   return STATUS_ORDER.map((status) => ({
      key: status,
      cards: cards.filter((c) => c.status === status),
   }));
}

/** Number of active filters (query counts as one). */
export function countActiveFilters(view: ICardViewState): number {
   let count = 0;
   if (view.query.trim().length > 0) {
      count++;
   }
   if (view.filterPriority != null) {
      count++;
   }
   if (view.filterLabelId != null) {
      count++;
   }
   if (view.filterAssigneeId != null) {
      count++;
   }
   return count;
}
